import { hostKind } from "@/lib/hostKind";
import { Link } from "@tanstack/react-router";
import { DocsMarkdown } from "@/features/docs/DocsMarkdown";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import { LEGAL_DOCS, type LegalDocId } from "./legalContent";

/**
 * Renders a public legal document (`/privacy`, `/terms`).
 *
 * Deliberately host- and auth-agnostic: unlike almost every other surface,
 * these two must render identically for an anonymous visitor on the marketing
 * apex, on a club host, and on the operator host. A user cannot be asked to
 * accept terms they are unable to read, and a privacy policy that 404s for the
 * signed-out is worthless.
 *
 * Two consequences worth keeping in mind when editing:
 *   - `HostGuard`'s admin-host allowlist must include these paths, or the
 *     operator host bounces them to `/super-admin` (see `isAdminHostAllowed`).
 *   - No `RequireClubHost` wrapper. There is no club-scoped variant of a
 *     platform-wide legal document.
 */
export function LegalPage({ docId }: { docId: LegalDocId }) {
  const doc = LEGAL_DOCS[docId];

  useDocumentMeta({
    title: doc.title,
    description: doc.description,
    ogType: "article",
  });

  return (
    <main
      data-testid={`legal-page-${doc.id}`}
      className="min-h-[60vh] bg-canvas px-6 py-10"
    >
      {hostKind() !== "admin" ? (
        <header className="legal-intro">
          <p className="eyebrow">Tktic · closed beta</p>
          <p className="legal-title">
            {docId === "privacy" ? "Privacy notice." : "Beta agreement."}
          </p>
          <p className="text-ink-muted">{doc.description}</p>
          <nav aria-label="Legal documents">
            <Link
              to="/privacy"
              aria-current={docId === "privacy" ? "page" : undefined}
            >
              Privacy notice
            </Link>
            <Link
              to="/terms"
              aria-current={docId === "terms" ? "page" : undefined}
            >
              Beta agreement
            </Link>
          </nav>
        </header>
      ) : null}
      <div className="mx-auto w-full max-w-3xl">
        <DocsMarkdown body={doc.body} />
        <div className="mt-10 border-t border-rule pt-6">
          <Link
            to="/"
            data-testid={`legal-${doc.id}-home-link`}
            className="font-display text-sm font-semibold text-ink underline decoration-accent decoration-2 underline-offset-4 hover:decoration-[3px]"
          >
            ← Back to home
          </Link>
        </div>
      </div>
    </main>
  );
}

export function PrivacyPage() {
  return <LegalPage docId="privacy" />;
}

export function TermsPage() {
  return <LegalPage docId="terms" />;
}
